import React from 'react';
import { Link } from 'react-router-dom';
import { getGraphSimilar } from '../services/api';
import { useRegion } from '../contexts/RegionContext';
import { pickProviderBlock } from '../services/regions';
import { contentIntelligenceAvailableFor, titleIntelligencePath } from '../services/titleLinks';

const MovieModal = ({ movie: initialMovie, onClose }) => {
  // Track the currently-shown title in state so clicking a "More like this" tile
  // swaps the modal to that title in place (the prop just seeds it).
  const [movie, setMovie] = React.useState(initialMovie);
  const { region } = useRegion();
  const [similar, setSimilar] = React.useState([]);
  const scrollRef = React.useRef(null);

  React.useEffect(() => { setMovie(initialMovie); }, [initialMovie]);

  React.useEffect(() => {
    const id = movie && (movie.tmdb_id || movie.id);
    if (!id) { setSimilar([]); return; }
    let alive = true;
    getGraphSimilar(id, 12).then((rows) => { if (alive) setSimilar(rows || []); });
    return () => { alive = false; };
  }, [movie]);

  if (!movie) return null;

  // Get title (works for both movies and series)
  const displayTitle = movie.title || movie.name;

  // Get content type
  const contentType = movie.type || 'movie';
  const isMovie = contentType === 'movie';

  const backdropUrl = movie.backdrop_path
    ? `https://image.tmdb.org/t/p/original${movie.backdrop_path}`
    : null;

  const formatRuntime = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getCertification = () => {
    // Return US certification if available
    return movie.certification || movie.content_rating || null;
  };

  const renderExternalLinks = () => {
    const { external_ids } = movie;
    if (!external_ids) return null;

    const links = [
      { id: 'imdb_id', url: 'https://www.imdb.com/title/', icon: 'IMDb', label: 'IMDb' },
      { id: 'instagram_id', url: 'https://instagram.com/', icon: 'IG', label: 'Instagram' },
      { id: 'twitter_id', url: 'https://twitter.com/', icon: 'TW', label: 'Twitter' },
      { id: 'facebook_id', url: 'https://facebook.com/', icon: 'FB', label: 'Facebook' },
    ];

    const activeLinks = links.filter(link => external_ids[link.id]);

    if (activeLinks.length === 0) return null;

    return (
      <div className="flex gap-4 mt-4">
        {activeLinks.map(link => (
          <a
            key={link.id}
            href={`${link.url}${external_ids[link.id]}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-white transition-colors"
            title={link.label}
          >
            {/* Simple SVGs for icons */}
            {link.icon === 'IMDb' && (
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M13.6 3H16v18h-2.4V3zm-9.8 0H1.4v18h2.4V3zm16.4 0H22.6v18h-2.4V3zM6.6 3h4.2c1.4.1 2.3 1.2 2.3 2.7v12.7c-.1 1.4-1 2.5-2.3 2.6H6.6V3zm2.4 2.3v13.3h1.4c.2-.1.4-.7.4-1.9V7.1c0-1.1-.1-1.7-.4-1.8H9z" /></svg>
            )}
            {link.icon === 'IG' && (
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.8.3 2.2.5.5.2.9.5 1.4.9.4.4.7.9.9 1.4.2.4.4 1 .5 2.2.1 1.2.1 1.6.1 4.8s0 3.6-.1 4.9c-.1 1.2-.3 1.8-.5 2.2-.2.5-.5.9-.9 1.4-.4.4-.9.7-1.4.9-.4.2-1 .4-2.2.5-1.2.1-1.6.1-4.8.1s-3.6 0-4.9-.1c-1.2-.1-1.8-.3-2.2-.5-.5-.2-.9-.5-1.4-.9-.4-.4-.7-.9-.9-1.4-.2-.4-.4-1-.5-2.2-.1-1.2-.1-1.6-.1-4.8s0-3.6.1-4.9c.1-1.2.3-1.8.5-2.2.2-.5.5-.9.9-1.4.4-.4.9-.7 1.4-.9.4-.2 1-.4 2.2-.5 1.3-.1 1.6-.1 4.9-.1zm0-2.2C8.7 0 8.3 0 7.1.1 5.8.1 4.9.3 4.1.6c-.8.3-1.5.7-2.1 1.3-.6.6-1 1.3-1.3 2.1-.3.8-.5 1.7-.6 2.9C.1 8.3 0 8.7 0 12s.1 3.7.1 4.9c.1 1.2.3 2.1.6 2.9.3.8.7 1.5 1.3 2.1.6.6 1.3 1 2.1 1.3.8.3 1.7.5 2.9.6 1.2.1 1.6.1 4.9.1s3.7-.1 4.9-.1c1.2-.1 2.1-.3 2.9-.6.8-.3 1.5-.7 2.1-1.3.6-.6 1-1.3 1.3-2.1.3-.8.5-1.7.6-2.9.1-1.2.1-1.6.1-4.9s-.1-3.7-.1-4.9c-.1-1.2-.3-2.1-.6-2.9-.3-.8-.7-1.5-1.3-2.1-.6-.6-1.3-1-2.1-1.3-.8-.3-1.7-.5-2.9-.6C15.7 0 15.3 0 12 0zm0 5.8c-3.4 0-6.2 2.8-6.2 6.2s2.8 6.2 6.2 6.2 6.2-2.8 6.2-6.2-2.8-6.2-6.2-6.2zm0 10.2c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4zm6.4-10.4c0 .8-.6 1.4-1.4 1.4-.8 0-1.4-.6-1.4-1.4 0-.8.6-1.4 1.4-1.4.8 0 1.4.6 1.4 1.4z" /></svg>
            )}
            {link.icon === 'TW' && (
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M18.2 4H22l-8.4 9.6 9.8 13.9H13l-6.1-8-7 8H3.8l9.1-10.4L3.4 4h10.5l5.3 7 4.9-7h-5.9zM17 21h2L6.1 6H4.1l12.9 15z" /></svg>
            )}
            {link.icon === 'FB' && (
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V1.74c-.25-.03-1.12-.11-2.12-.11-2.1 0-3.53 1.29-3.53 3.66v2.45h-2.37v4h2.37v10.26h4.1V13.5z" /></svg>
            )}
          </a>
        ))}
      </div>
    );
  };


  const renderOfficialTrailer = () => {
    // Resolve a YouTube video key from any of the shapes our data uses:
    //  - movie.trailer_key            (singular key from /search — the common case)
    //  - movie.trailers.official_trailer.youtube_key / .key
    //  - movie.trailers.official_trailer.youtube_embed (full embed URL → extract id)
    const official = movie.trailers?.official_trailer;
    let videoKey =
      movie.trailer_key ||
      official?.youtube_key ||
      official?.key ||
      null;
    if (!videoKey && official?.youtube_embed) {
      const m = official.youtube_embed.match(/(?:embed\/|v=|youtu\.be\/)([\w-]{6,})/);
      if (m) videoKey = m[1];
    }

    if (!videoKey) {
      const year = movie.release_date ? new Date(movie.release_date).getFullYear() : '';
      const searchQuery = encodeURIComponent(`${displayTitle} ${year} trailer`);
      const searchUrl = `https://www.youtube.com/results?search_query=${searchQuery}`;

      return (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Trailer</h3>
          <div className="bg-gray-50 rounded-lg p-8 text-center border-2 border-dashed border-gray-200">
            <p className="text-gray-500 mb-4">Official trailer not available in our database.</p>
            <a
              href={searchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-[#FF0000] text-white font-medium rounded-lg hover:bg-[#CC0000] transition-colors shadow-sm"
            >
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
              </svg>
              Search on YouTube
            </a>
          </div>
        </div>
      );
    }

    // Play inline on the platform — no route-out to YouTube.
    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Official Trailer</h3>
        <div className="relative" style={{ paddingBottom: '56.25%' }}>
          <iframe
            className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
            src={`https://www.youtube.com/embed/${videoKey}?autoplay=1&mute=1&rel=0&modestbranding=1`}
            title={`${displayTitle} — Official Trailer`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </div>
    );
  };

  const renderWatchProviders = () => {
    const providers = movie.watch_providers;
    if (!providers || Object.keys(providers).length === 0) return null;

    // Prefer the selected region's providers, else fall back to US/first available.
    const regionProviders = pickProviderBlock(providers, region);
    if (!regionProviders) return null;

    const streamingProviders = regionProviders.flatrate || [];
    if (streamingProviders.length === 0) return null;

    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Where to Watch</h3>
        <div className="flex flex-wrap gap-3">
          {streamingProviders.map((provider, index) => (
            <div
              key={index}
              className="flex items-center bg-gray-100 rounded-lg px-4 py-2"
            >
              {provider.logo_path && (
                <img
                  src={`https://image.tmdb.org/t/p/w92${provider.logo_path}`}
                  alt={provider.provider_name}
                  className="w-8 h-8 rounded mr-2"
                />
              )}
              <span className="text-sm font-medium text-gray-900">
                {provider.provider_name}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-75 z-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal container */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Backdrop image header */}
            {backdropUrl && (
              <div className="relative h-96 w-full overflow-hidden rounded-t-2xl">
                <img
                  src={backdropUrl}
                  alt={displayTitle}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  {/* Type Badge */}
                  <div className="mb-3">
                    <span className={`inline-block px-3 py-1.5 text-sm font-bold rounded-lg ${isMovie
                        ? 'bg-blue-600 text-white'
                        : 'bg-purple-600 text-white'
                      }`}>
                      {isMovie ? '🎬 MOVIE' : '📺 TV SERIES'}
                    </span>
                  </div>
                  <h2 className="text-4xl font-bold text-white mb-2">
                    {displayTitle}
                  </h2>
                  <div className="flex items-center space-x-4 text-white">
                    <span className="flex items-center">
                      <svg
                        className="w-5 h-5 text-yellow-400 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      {movie.vote_average?.toFixed(1)}
                    </span>
                    {movie.release_date && (
                      <span>{new Date(movie.release_date).getFullYear()}</span>
                    )}
                    {movie.runtime && <span>{formatRuntime(movie.runtime)}</span>}
                    {/* Certification Pill */}
                    {getCertification() && (
                      <span className="ml-2 px-2 py-0.5 border border-white/40 rounded text-sm text-white/90 uppercase backdrop-blur-sm">
                        {getCertification()}
                      </span>
                    )}
                    {/* Season Count for TV */}
                    {movie.number_of_seasons > 0 && (
                      <span className="ml-2">{movie.number_of_seasons} Season{movie.number_of_seasons > 1 ? 's' : ''}</span>
                    )}
                  </div>

                  {/* Tagline */}
                  {movie.tagline && (
                    <p className="text-lg italic text-gray-300 mt-2 font-light">
                      "{movie.tagline}"
                    </p>
                  )}
                  {/* External Links */}
                  {renderExternalLinks()}
                </div>
              </div>
            )}


            {/* Content */}
            <div className="p-8">
              {contentIntelligenceAvailableFor(movie) && <Link
                to={titleIntelligencePath(movie)}
                className="mb-6 inline-flex items-center rounded-full bg-[#141114] px-5 py-2.5 text-sm font-semibold text-[#F1ECE2] hover:bg-[#332A24] transition-colors"
              >
                Explore Content Intelligence →
              </Link>}
              {/* If no backdrop, show title here */}
              {!backdropUrl && (
                <div className="mb-6">
                  {/* Type Badge (No backdrop) */}
                  <div className="mb-3">
                    <span className={`inline-block px-3 py-1.5 text-sm font-bold rounded-lg ${isMovie
                        ? 'bg-blue-600 text-white'
                        : 'bg-purple-600 text-white'
                      }`}>
                      {isMovie ? '🎬 MOVIE' : '📺 TV SERIES'}
                    </span>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    {displayTitle}
                  </h2>
                  <div className="flex items-center space-x-4 text-gray-600">
                    <span className="flex items-center">
                      <svg
                        className="w-5 h-5 text-yellow-400 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      {movie.vote_average?.toFixed(1)}
                    </span>
                    {movie.release_date && (
                      <span>{new Date(movie.release_date).getFullYear()}</span>
                    )}
                    {movie.runtime && <span>{formatRuntime(movie.runtime)}</span>}
                    {/* Certification Pill (No backdrop) */}
                    {getCertification() && (
                      <span className="px-2 py-0.5 border border-gray-400 rounded text-sm text-gray-600 border-gray-300 uppercase">
                        {getCertification()}
                      </span>
                    )}
                    {/* Season Count for TV (No backdrop) */}
                    {movie.number_of_seasons > 0 && (
                      <span>{movie.number_of_seasons} Season{movie.number_of_seasons > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  {movie.tagline && (
                    <p className="text-lg italic text-gray-500 mt-2 font-light">
                      "{movie.tagline}"
                    </p>
                  )}
                </div>
              )}


              {/* Genres */}
              {movie.genres && movie.genres.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {movie.genres.map((genre, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-medium"
                    >
                      {genre}
                    </span>
                  ))}
                </div>
              )}

              {/* Official Trailer */}
              {renderOfficialTrailer()}

              {/* Watch Providers */}
              {renderWatchProviders()}

              {/* Overview */}
              {movie.overview && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Overview
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{movie.overview}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Director (Movies) or Creators (Series) */}
                {movie.director && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Director
                    </h3>
                    <p className="text-gray-700">{movie.director}</p>
                  </div>
                )}

                {/* Creators for TV Series */}
                {!isMovie && movie.creators && movie.creators.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Creators
                    </h3>
                    <p className="text-gray-700">{movie.creators.join(', ')}</p>
                  </div>
                )}

                {/* Cast */}
                {movie.cast && movie.cast.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Cast
                    </h3>
                    <p className="text-gray-700">{movie.cast.slice(0, 10).join(', ')}</p>
                  </div>
                )}

                {/* Budget & Revenue */}
                {(movie.budget || movie.revenue) && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Box Office
                    </h3>
                    {movie.budget && (
                      <p className="text-gray-700">
                        <span className="font-medium">Budget:</span>{' '}
                        {formatCurrency(movie.budget)}
                      </p>
                    )}
                    {movie.revenue && (
                      <p className="text-gray-700">
                        <span className="font-medium">Revenue:</span>{' '}
                        {formatCurrency(movie.revenue)}
                      </p>
                    )}
                  </div>
                )}

                {/* Keywords */}
                {movie.keywords && movie.keywords.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Keywords
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {movie.keywords.slice(0, 10).map((keyword, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Languages */}
                {movie.languages && movie.languages.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Languages
                    </h3>
                    <p className="text-gray-700">{movie.languages.join(', ')}</p>
                  </div>
                )}

                {/* Countries */}
                {movie.countries && movie.countries.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Production Countries
                    </h3>
                    <p className="text-gray-700">{movie.countries.join(', ')}</p>
                  </div>
                )}

                {/* Networks for TV Series */}
                {!isMovie && movie.networks && movie.networks.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Networks
                    </h3>
                    <p className="text-gray-700">{movie.networks.join(', ')}</p>
                  </div>
                )}

                {/* Episode Count for TV Series */}
                {!isMovie && movie.number_of_episodes && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Episodes
                    </h3>
                    <p className="text-gray-700">
                      {movie.number_of_episodes} episodes across {movie.number_of_seasons} season{movie.number_of_seasons > 1 ? 's' : ''}
                    </p>
                  </div>
                )}
              </div>

              {/* More like this — relational neighbours from the knowledge graph */}
              {similar && similar.length > 0 && (
                <div className="mt-8" ref={scrollRef}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">More like this</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    From the knowledge graph — shared cast, themes &amp; genres
                  </p>
                  <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                    {similar.map((s) => (
                      <button
                        key={s.tmdb_id || s.id}
                        onClick={() => { setMovie(s); if (scrollRef.current) scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                        className="flex-shrink-0 w-28 text-left group"
                        title={s.why || ''}
                      >
                        <img
                          src={s.poster_path ? `https://image.tmdb.org/t/p/w185${s.poster_path}` : '/placeholder-poster.png'}
                          alt={s.title || s.name}
                          loading="lazy"
                          className="w-28 h-40 object-cover rounded-lg shadow-sm group-hover:ring-2 group-hover:ring-purple-400 transition"
                        />
                        <div className="mt-1 text-xs font-medium text-gray-800 line-clamp-2 leading-tight">
                          {s.title || s.name}
                        </div>
                        {s.why && (
                          <div className="text-[10px] text-gray-500 line-clamp-2 leading-tight">{s.why}</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MovieModal;
